/**
 * Meframe - Main entry point for the media processing framework
 */

import type { MeframeConfig, ResolvedConfig, MeframeState, ExportOptions } from './types';
import type { CompositionModelData, CompositionPatch, TimeUs } from './model/types';
import type { PreviewHandle } from './controllers/types';
import type { Plugin } from './plugins/types';
import { CompositionModel } from './model/CompositionModel';
import { loadConfig } from './config';
import { Orchestrator } from './orchestrator/Orchestrator';
import { PlaybackController } from './controllers/PlaybackController';
import { PreRenderService } from './controllers/PreRenderService';
import { PluginManager } from './plugins/PluginManager';
import { EventBus } from './event/EventBus';
import { MeframeEvent, type EventPayloadMap } from './event/events';

export class Meframe {
  /** Current state - managed internally via setState() */
  state: MeframeState = 'idle';
  /** Configuration - immutable after construction */
  readonly config: ResolvedConfig;
  /** Composition model - managed via setCompositionTree() */
  model: CompositionModel | null = null;
  /** Plugin manager for extensions */
  readonly pluginManager: PluginManager;
  /** Event bus for subscribing to Meframe events */
  readonly events: Pick<EventBus<EventPayloadMap>, 'on' | 'off' | 'once'>;

  private orchestrator: Orchestrator;
  private eventBus: EventBus<EventPayloadMap>;
  private playbackController: PlaybackController | null = null;
  private preRenderService: PreRenderService | null = null;
  private canvas?: HTMLCanvasElement | OffscreenCanvas;

  private constructor(config: ResolvedConfig) {
    this.config = config;
    this.eventBus = new EventBus<EventPayloadMap>();
    this.events = this.eventBus.asReadonly();
    this.pluginManager = new PluginManager(this);

    // Initialize orchestrator with configuration and shared eventBus
    // Worker paths are passed via config
    this.orchestrator = new Orchestrator({
      maxWorkers: (config as any).maxWorkers,
      cacheConfig: config.cache as any,
      eventBus: this.eventBus,
      workerPath: config.global.workerPath,
      workerExtension: config.global.workerExtension,
    });
  }

  /**
   * Create a new Meframe instance
   */
  static async create(config: MeframeConfig = {}): Promise<Meframe> {
    // Load and resolve configuration using ConfigLoader
    const resolvedConfig = await loadConfig({ override: config });
    const instance = new Meframe(resolvedConfig);
    await instance.initialize();
    return instance;
  }

  /**
   * Initialize the core engine
   */
  private async initialize(): Promise<void> {
    this.setState('loading');

    try {
      // Initialize orchestrator (sets up workers and cache)
      await this.orchestrator.initialize();

      // Initialize plugins if provided
      const plugins = (this.config as any).plugins;
      if (plugins && Array.isArray(plugins)) {
        for (const plugin of plugins) {
          // Ensure plugin type matches the Plugin interface
          if (plugin && typeof plugin === 'object' && 'name' in plugin && 'install' in plugin) {
            this.pluginManager.register(plugin as Plugin);
          }
        }
      }

      this.setState('idle');
    } catch (error) {
      this.setState('error');
      throw error;
    }
  }

  /**
   * Set the composition model
   */
  async setCompositionModel(model: CompositionModel | CompositionModelData): Promise<void> {
    this.ensureNotDestroyed();

    // Convert plain object to CompositionModel instance if needed
    const compositionModel =
      model instanceof CompositionModel ? model : new CompositionModel(model);

    // Set the model in orchestrator
    await this.orchestrator.setCompositionModel(compositionModel);
    this.model = compositionModel;
    this.setState('ready');
    this.eventBus.emit(MeframeEvent.Ready, {
      trackCount: model.tracks.length,
      clipCount: model.tracks.reduce(
        (acc: number, track: any) => acc + track.clips?.length || 0,
        0
      ),
      durationUs: model.durationUs,
    });
  }

  /**
   * Apply a patch to the composition model
   */
  async applyPatch(patch: CompositionPatch): Promise<void> {
    this.ensureNotDestroyed();

    if (!this.model) {
      throw new Error('No composition model set');
    }

    // Apply patch through orchestrator
    await this.orchestrator.applyPatch(patch);

    // Patch is applied to the model by orchestrator
    this.model = this.orchestrator.compositionModel!;
  }

  /**
   * Start preview and return a handle for control
   */
  startPreview(options?: {
    canvas?: HTMLCanvasElement | OffscreenCanvas;
    startUs?: TimeUs;
  }): PreviewHandle {
    this.ensureReady();

    // Use provided canvas or the one from config
    const canvas = options?.canvas || this.canvas;
    if (!canvas) {
      throw new Error('Canvas is required for preview');
    }

    // Store canvas for later use
    this.canvas = canvas;

    // Create playback controller
    if (!this.playbackController) {
      this.playbackController = new PlaybackController(this.orchestrator as any, this.eventBus, {
        canvas,
        startUs: options?.startUs,
      });
      this.playbackController.setAudioSession(this.orchestrator.audioSession);
    }

    // Start pre-render service for background caching
    if (!this.preRenderService) {
      this.preRenderService = new PreRenderService(this.orchestrator as any, this.events);
      this.preRenderService.start();
    }

    // Connect playback controller to pre-render service
    // Update pre-render position when playback position changes
    this.playbackController.on(MeframeEvent.PlaybackTimeUpdate, (payload: any) => {
      this.preRenderService?.updatePlaybackTime(payload.timeUs);
    });

    // Update playback active state
    this.playbackController.on(MeframeEvent.PlaybackPlay, () => {
      this.preRenderService?.setPlaybackActive(true);
    });

    this.playbackController.on(MeframeEvent.PlaybackPause, () => {
      this.preRenderService?.setPlaybackActive(false);
    });

    this.playbackController.on(MeframeEvent.PlaybackStop, () => {
      this.preRenderService?.setPlaybackActive(false);
    });

    this.playbackController?.renderCurrentFrame(0);

    // Return preview handle
    return this.playbackController;
  }

  /**
   * Export the composition
   * Uses L2 cache for fast export, muxes in main thread
   */
  async export(options: ExportOptions): Promise<Blob> {
    this.ensureReady();
    this.setState('exporting');

    try {
      const model = this.model;
      if (!model) {
        throw new Error('No composition model set');
      }

      // 1. Check L2 cache readiness
      const readiness = await this.getExportReadiness();

      if (!readiness.ready) {
        // Emit preparing event
        this.eventBus.emit(MeframeEvent.ExportPreparing, {
          totalClips: readiness.totalClips,
          cachedClips: readiness.cachedClips,
          missingClips: readiness.missingClips,
        });

        // Ensure PreRenderService is started
        if (!this.preRenderService) {
          this.preRenderService = new PreRenderService(this.orchestrator as any, this.events);
          this.preRenderService.start();
        }

        // Create AbortController for timeout control
        const abortController = new AbortController();
        const timeoutMs = 5 * 60 * 1000; // 5 minutes
        const timeoutId = setTimeout(() => {
          abortController.abort();
        }, timeoutMs);

        try {
          // Actively trigger missing clips L2 rendering
          await this.preRenderService.ensureClipsInL2(readiness.missingClips, {
            onProgress: (completed, total) => {
              this.eventBus.emit(MeframeEvent.ExportProgress, {
                progress: completed / total,
                stage: 'preparing',
                message: `Preparing cache: ${completed}/${total} clips`,
              });
            },
            signal: abortController.signal,
          });
        } catch (error) {
          if (error instanceof DOMException && error.name === 'AbortError') {
            throw new Error(`Export preparation timeout after ${timeoutMs / 1000}s`);
          }
          throw error;
        } finally {
          clearTimeout(timeoutId);
        }
      }

      // 2. Continue normal export flow
      const width = options.width || (model as any).renderConfig?.width || 720;
      const height = options.height || (model as any).renderConfig?.height || 1280;
      const fps = options.fps || model.fps || 30;

      this.eventBus.emit(MeframeEvent.ExportStart, {
        format: options.format || 'mp4',
        width,
        height,
        fps,
        durationUs: model.durationUs,
      });

      // Delegate to orchestrator
      const blob = await this.orchestrator.export(model, options);

      this.setState('ready');
      this.eventBus.emit(MeframeEvent.ExportComplete, {
        size: blob.size,
        durationMs: model.durationUs / 1000,
        format: options.format || 'mp4',
      });
      this.eventBus.emit(MeframeEvent.ExportProgress, {
        progress: 1,
      });

      return blob;
    } catch (error) {
      this.setState('error');
      this.eventBus.emit(MeframeEvent.ExportError, {
        error: error as Error,
        stage: 'export',
      });
      throw error;
    }
  }

  /**
   * Check export readiness
   * Returns coverage info for L2 cache
   */
  async getExportReadiness(): Promise<{
    ready: boolean;
    totalClips: number;
    cachedClips: number;
    missingClips: string[];
    coverage: number;
  }> {
    if (!this.model) {
      return {
        ready: false,
        totalClips: 0,
        cachedClips: 0,
        missingClips: [],
        coverage: 0,
      };
    }

    // Only check main track clips (attachments are already included)
    const mainTrack = this.model.findTrack(this.model.mainTrackId);
    const allClips = mainTrack?.clips ?? [];

    const missingClips: string[] = [];
    let cachedCount = 0;

    for (const clip of allClips) {
      const inL2 = await this.orchestrator.cacheManager.hasClipInL2(clip.id, 'video');
      if (inL2) {
        cachedCount++;
      } else {
        missingClips.push(clip.id);
      }
    }

    return {
      ready: missingClips.length === 0,
      totalClips: allClips.length,
      cachedClips: cachedCount,
      missingClips,
      coverage: allClips.length > 0 ? cachedCount / allClips.length : 0,
    };
  }

  /**
   * Get current playback time
   */
  getCurrentTime(): number {
    return this.playbackController?.currentTimeUs || 0;
  }

  /**
   * Clear all L1/L2 cache data
   * Useful for debugging or forcing re-render
   */
  async clearCache(): Promise<void> {
    this.ensureReady();

    // Stop playback first
    this.playbackController?.stop();

    // Stop pre-render service
    this.preRenderService?.stop();

    // Clear cache through CacheManager
    await this.orchestrator.cacheManager.clear();

    console.log('[Meframe] Cache cleared successfully');
  }

  /**
   * Clean up and destroy the instance
   */
  async dispose(): Promise<void> {
    if (this.state === 'destroyed') return;

    // Stop playback
    this.playbackController?.stop();
    this.playbackController?.dispose();
    this.playbackController = null;

    // Stop pre-render service
    this.preRenderService?.stop();
    this.preRenderService = null;

    // Cleanup plugins
    await this.pluginManager.disposeAll();

    // Dispose orchestrator (stops workers and clears cache)
    await this.orchestrator.dispose();

    // Clear event bus
    this.eventBus.dispose();

    this.setState('destroyed');
  }

  /**
   * Set internal state
   */
  private setState(state: MeframeState): void {
    const oldState = this.state;
    this.state = state;

    // Emit state change event
    // Use a generic event for now, can be added to MeframeEvent enum later
    this.eventBus.emit('state:changed' as any, {
      oldState,
      newState: state,
    });
  }

  /**
   * Ensure the instance is not destroyed
   */
  private ensureNotDestroyed(): void {
    if (this.state === 'destroyed') {
      throw new Error('Core instance is destroyed');
    }
  }

  /**
   * Ensure the instance is ready
   */
  private ensureReady(): void {
    this.ensureNotDestroyed();

    if (!this.model) {
      throw new Error('No composition model set');
    }

    if (this.state === 'loading' || this.state === 'idle') {
      throw new Error('Core is not ready');
    }
  }
}
